/**
 * @file index.tsx
 *
 * @fileoverview Renders a autocomplete facet component that has search functionality built-in.
 */
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Facet, FacetValue } from '~/jobiqo/models/search';
import FacetMultiple from '~/jobiqo/components/Search/facets/facet-multiple';
import FacetSingle from '~/jobiqo/components/Search/facets/facet-single';
import { AutocompleteFacetPopover } from '~/vcl/molecules/07-search/02-facet/03-autocomplete-facet-popover';
import { flattenForChildren } from '~/jobiqo/services/taxonomyHelpers';
import { getCommonTranslations } from '~/jobiqo/utils/intl';
interface FacetAutocompleteProps {
  /**
   * The current querystring object.
   */
  querystring: any;
  /**
   * The current pathname.
   */
  pathname: string;
  /**
   * The index of the current facet item.
   */
  index: number;
  /**
   * The list of facets.
   */
  facets: Facet[];
  /**
   * The current facet.
   */
  facet: Facet;
  /**
   * The callback for when toggling a filter.
   */
  onFacetToggle: (
    name: string,
    selectedItems: string[],
    alias?: string,
  ) => void;
  /**
   * Gets the list of selected facets.
   */
  getFacetSelectedItems(facet: Facet): string[];
}

/**
 * Checks if label includes search value.
 * Case insensitive.
 * @param {string} label
 *  The label.
 * @param {string} searchVal
 *  The current search value.
 * @returns {boolean}
 *  True if label matches search value.
 */
function isLabelMatch(label: string, searchVal: string): boolean {
  return (
    !!label &&
    !!searchVal &&
    label.toLowerCase().includes(searchVal.toLowerCase())
  );
}

export default function FacetAutocomplete({
  querystring,
  pathname,
  index,
  facets,
  facet,
  onFacetToggle,
  getFacetSelectedItems,
}: FacetAutocompleteProps) {
  const intl = useIntl();
  const { CLOSE, TOGGLE } = getCommonTranslations();
  // Search values for every facet.
  const [searchValues, setSearchValues] = useState<string[]>(
    Array(facets.length).fill(''),
  );
  // Filtered values for every facet.
  const [filteredFacetValues, setFilteredFacetValues] = useState<
    FacetValue[][]
  >(facets.map(({ values }) => values));

  /**
   * Callback for when the user searches for a term inside a facet.
   *
   * Sets a list of all the search values for every facet. Every time a user changes the value of a
   * autocomplete facet we need to update the search values for that facet (and keep whatever is filtered on other facets).
   *
   * @param searchValue
   *   The current search value.
   * @param facetToSearchIndex
   *   The index of the facet to search.
   */
  const updateSearchValue = (
    searchValue: string,
    facetToSearchIndex: number,
  ) => {
    setSearchValues(
      searchValues.map((searchedValue, i) =>
        // Update only current (index) value, propagate the others.
        i === facetToSearchIndex ? searchValue : searchedValue,
      ),
    );

    setFilteredFacetValues(
      facets
        // Map values for every facet.
        .map(({ values }) => flattenForChildren(values)) // Flatten all values because we want to catch also sub elements.
        // Filter only current (index) facet values, propagate the others.
        .map((facetValues, i) => {
          return i === facetToSearchIndex
            ? facetValues.filter(({ label }) =>
                isLabelMatch(label, searchValue),
              )
            : filteredFacetValues[i];
        }),
    );
  };

  // Get the filtered list of facet values for a facet if something is currently
  // saved as a search for that facet (see searchValues).
  // The filtered facet values is what we pass to the actual filters.
  useEffect(() => {
    const newFacetValues = facets.map(({ values }) => {
      if (!searchValues[index]) {
        // Nothing changes if its not being searched.
        return values;
      }

      // Filter the values based on the search value.
      // if the element is being searched we are flattening the values
      // so we don't need to include children (children are already present).
      return flattenForChildren(values)
        .filter(({ label }) => isLabelMatch(label, searchValues[index]))
        .map((facetValue) => {
          return {
            ...facetValue,
            children: [],
          };
        });
    });

    setFilteredFacetValues(newFacetValues);
  }, [searchValues, facets]);

  if (!filteredFacetValues[index]) {
    return null;
  }

  return (
    <AutocompleteFacetPopover
      searchValue={searchValues[index]}
      onSearch={(val) => updateSearchValue(val, index)}
      onClear={() => updateSearchValue('', index)}
      closeLabel={CLOSE}
      toggleLabel={TOGGLE}
      placeholder={
        facet.title
          ? `${
              !!intl.messages &&
              intl.messages?.[`config_facet_${facet.name}_title`]
                ? intl.messages[`config_facet_${facet.name}_title`]
                : facet.title
            }${
              getFacetSelectedItems(facet).length > 0
                ? ` (${getFacetSelectedItems(facet).length})`
                : ''
            }`
          : facet.name
      }
    >
      {facet.multiple ? (
        <FacetMultiple
          querystring={querystring}
          onFacetToggle={(facetItem, selectedItems, alias) => {
            setSearchValues(
              searchValues.map((searchedValue, i) =>
                // Update only current (index) value, set to empty string.
                i === index ? '' : searchedValue,
              ),
            );
            // Clear any text entered for this facet.
            // setFilteredFacetValues([]);
            onFacetToggle(facetItem, selectedItems, alias);
          }}
          selectedItems={getFacetSelectedItems(facet)}
          facet={{ ...facet, values: filteredFacetValues[index] }}
        />
      ) : (
        <FacetSingle
          querystring={querystring}
          pathname={pathname}
          selectedItems={getFacetSelectedItems(facet)}
          facet={{ ...facet, values: filteredFacetValues[index] }}
          onFacetToggle={(facetItem, selectedItems, alias) => {
            setSearchValues(
              searchValues.map((searchedValue, i) =>
                // Update only current (index) value, set to empty string.
                i === index ? '' : searchedValue,
              ),
            );
            onFacetToggle(facetItem, selectedItems, alias);
          }}
        />
      )}
    </AutocompleteFacetPopover>
  );
}
